import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import {
  ProductNavParams,
  ProductScreens,
} from '@euler/app/flows/products/screens';
import { Aside } from '@euler/components/Aside';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { ErrorFallback } from '@euler/components/error';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { FontFamily } from '@euler/components/typography';
import { adaptiveWrapperOptions } from '@euler/functions/navigationOptionsBuilder';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { TaskProductManifestItem } from '@euler/model/task-detail';
import {
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { memo } from 'react';
import { Observable } from 'rxjs';
import { useTheme } from 'styled-components';

const Stack = createStackNavigator();

type Props = {
  items$: Observable<TaskProductManifestItem[]>;
  onRemove?: (item: TaskProductManifestItem) => void;
  onEditCloser?: (item: TaskProductManifestItem) => void;
  screen?: keyof ProductNavParams;
  params?: Record<string, any>;
};

export const ProductFlow = wrapNavigatorScreen(
  (props: Props) => {
    const isMobile = useIsMobileLayout();

    if (isMobile) {
      return <Content {...props} />;
    }

    return (
      <Aside dismissible>
        <Content {...props} />
      </Aside>
    );
  },
  adaptiveWrapperOptions<any>(() => ({
    headerShown: false,
  })),
);

const Content = memo((props: Props) => {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  return (
    <AppNavigationProvider>
      <LayoutProviderView fallbackToChildren style={{ flex: 1 }}>
        <Sentry.ErrorBoundary
          // @ts-ignore
          fallback={(params: any) => <ErrorFallback {...params} />}
        >
          <Stack.Navigator
            initialRouteName={'_productManifest'}
            screenOptions={{
              ...TransitionPresets.SlideFromRightIOS,
              headerMode: 'float',
              headerTitleAlign: 'center',
              headerBackTitle: ' ',
              headerTitleStyle: {
                fontSize: rpx(18),
                fontFamily: FontFamily.NotoSans.Bold,
              },
              cardStyle: {
                backgroundColor: theme.page.card.backgroundColor,
              },
              headerTitle: MultilineHeaderTitle,
              headerBackImage: () => <BackIcon />,
            }}
          >
            {spreadScreens(
              Stack.Screen,
              ProductScreens,
              '_productManifest',
              props,
            )}
          </Stack.Navigator>
        </Sentry.ErrorBoundary>
      </LayoutProviderView>
    </AppNavigationProvider>
  );
});
